import React, { useId, useMemo } from 'react'

import { conditionallyLocalizeDate } from 'utils'
import type { MembersEnrollmentOverviewProgressState, MembersEnrollmentProgressEventLabel, MembersEnrollmentProgressEventState } from 'utils/web_api.schemas'

type TrackerSegmentProps = {
  state: MembersEnrollmentProgressEventState
  label: MembersEnrollmentProgressEventLabel
  completionDate?: Date
  progressState: MembersEnrollmentOverviewProgressState
}

// SVG Constants
const SVG_DIMENSIONS = { width: 94, height: 8 }
const SVG_PROPS = {
  xmlns: 'http://www.w3.org/2000/svg',
  height: SVG_DIMENSIONS.height,
  viewBox: `0 0 ${SVG_DIMENSIONS.width} ${SVG_DIMENSIONS.height}`,
  fill: 'none',
  preserveAspectRatio: 'none'
}
const SVG_PATHS = {
  pending: 'M92.3281 0V0.5H92.4316L92.2842 0.735352L92.708 1L91.458 3L91.0342 2.73535L89.7842 4.73535L90.208 5L88.958 7L88.5342 6.73535L88.0566 7.5H87.3291V8H85.3223V7.5H83.3145V8H81.3066V7.5H79.2988V8H77.292V7.5H75.2842V8H73.2764V7.5H71.2686V8H69.2617V7.5H67.2539V8H65.2461V7.5H63.2383V8H61.2314V7.5H59.2236V8H57.2158V7.5H55.208V8H53.2012V7.5H51.1934V8H49.1855V7.5H47.1777V8H45.1709V7.5H43.1631V8H41.1553V7.5H39.1475V8H37.1406V7.5H35.1328V8H33.125V7.5H31.1172V8H29.1094V7.5H27.1025V8H25.0947V7.5H23.0869V8H21.0791V7.5H19.0723V8H17.0645V7.5H15.0566V8H13.0488V7.5H11.042V8H9.03418V7.5H7.02637V8H5.01855V7.5H3.01172V8H1.00391V7.5H0.889648L1.03516 7.25977L0.607422 7L1.82227 4.99902L1.82324 5L2.25 5.25977L3.46582 3.25977L3.03809 3L4.25293 0.999023L4.25391 1L4.68066 1.25977L5.14258 0.5H5.86621V0H7.87695V0.5H9.8877V0H11.8984V0.5H13.9092V0H15.9199V0.5H17.9307V0H19.9414V0.5H21.9521V0H23.9629V0.5H25.9736V0H27.9844V0.5H29.9951V0H32.0059V0.5H34.0166V0H36.0273V0.5H38.0381V0H40.0488V0.5H42.0596V0H44.0703V0.5H46.0811V0H48.0918V0.5H50.1025V0H52.1133V0.5H54.124V0H56.1348V0.5H58.1455V0H60.1562V0.5H62.167V0H64.1777V0.5H66.1885V0H68.1992V0.5H70.21V0H72.2207V0.5H74.2314V0H76.2422V0.5H78.2529V0H80.2637V0.5H82.2744V0H84.2852V0.5H86.2959V0H88.3066V0.5H90.3174V0H92.3281Z',
  processing: 'M93.1108 0.5L88.8569 7.5H1.55615L5.80908 0.5H93.1108Z',
  completed: 'M5.6665 0H93.9998L89.1387 8H0.666504L5.6665 0Z'
}

const TrackerSegment: React.FC<TrackerSegmentProps> = ({
  state,
  label,
  completionDate,
  progressState
}) => {
  const { strokeAndFillColor, processingText } = useMemo(() => {
    let strokeAndFillColor: string
    let processingText: string

    switch (progressState) {
      case 'needs_attention':
        strokeAndFillColor = 'orange'
        processingText = 'Needs attention'
        break
      case 'failed':
        strokeAndFillColor = 'red'
        processingText = 'Failed'
        break
      default:
        strokeAndFillColor = 'green'
        processingText = 'In progress'
    }

    return { strokeAndFillColor, processingText }
  }, [progressState])

  // SVG ids are globally unique, so we have to generate a new one for each instance
  // In theory we could put the green / orange / red stripes somewhere else (i.e., in the parent component),
  // but it's a lot of state to manage
  const stripePatternId = useId()

  let secondaryText = null

  if (state === 'completed' && completionDate) {
    secondaryText = conditionallyLocalizeDate(completionDate)
  } else if (state === 'processing') {
    secondaryText = processingText
  }

  const renderSVG = () => {
    switch (state) {
      case 'pending':
        return (
          <svg {...SVG_PROPS} className='text-gray-300 w-full'>
            <path
              d={SVG_PATHS.pending}
              fill='white'
              stroke='currentColor'
              strokeDasharray='2 2'
            />
          </svg>
        )

      case 'processing':
        return (
          <svg {...SVG_PROPS} className={`text-${strokeAndFillColor}-400 w-full`}>
            <defs>
              <pattern
                id={stripePatternId}
                patternUnits='userSpaceOnUse'
                width='5'
                height='8'
                patternTransform='rotate(30)'
              >
                <rect width='5' height='8' fill='white' />
                <rect width='1' height='8' fill='currentColor' />
              </pattern>
            </defs>
            <path
              d={SVG_PATHS.processing}
              fill={`url(#${stripePatternId})`}
              stroke='currentColor'
            />
          </svg>
        )

      case 'completed':
        return (
          <svg {...SVG_PROPS} className={`text-${strokeAndFillColor}-400 w-full`}>
            <path d={SVG_PATHS.completed} fill='currentColor' stroke='currentColor' />
          </svg>
        )

      default:
        return null
    }
  }

  return (
    <div className='flex flex-col items-start flex-1'>
      <div className='relative w-full'>
        {renderSVG()}
      </div>

      <div className='body-sm text-main mt-2'>
        {label}
      </div>

      {secondaryText && (
        <div className='caption text-gray-500 mt-1'>
          {secondaryText}
        </div>
      )}
    </div>
  )
}

export default TrackerSegment
